import * as yup from 'yup';
import { getTimezoneMismatch } from './getTimezoneMismatch';

// An empty field is no id (null)
const optionalId = (label) =>
	yup
		.number()
		.transform((value, originalValue) => (originalValue === '' ? null : value))
		.typeError(`${label} must be a number`)
		.integer(`${label} must be a whole number`)
		.positive(`${label} must be a positive number`)
		.nullable();

/**
 * A broker group as an admin edits it, kept the same as the backend's
 * services/validation/brokerGroup.js (the admin API also checks the ledger has Insight
 * credentials in Secret Manager, as only the backend can). Text is trimmed and the SharePoint domain
 * lowercased when cast.
 *
 * - name, ledger: required
 * - branchId, permissionsGroup: Insight ids, a positive whole number or empty (null)
 * - sharepointDomain: https://<tenant>.sharepoint.com, the tenant only letters and numbers
 * - clientSiteSuffix: empty (the root site) or /sites/<site>, one segment of letters,
 *   numbers, - and _
 * - clientFolder: the library name as shown in SharePoint (e.g. Client Documents), not
 *   URL-encoded and without /; the API saves it encoded
 * - country and timezone: both required, and the timezone must be one of the country's.
 *   state is optional, and only for a country with states (Australia)
 *
 * @param {object} countries from listCountries, keyed by country code
 */
export const getBrokerGroupValidationSchema = (countries) =>
	yup.object({
		name: yup.string().trim().required('Name cannot be empty'),
		ledger: yup.string().trim().required('Ledger cannot be empty'),
		branchId: optionalId('Insight branch id'),
		permissionsGroup: optionalId('Permissions group id'),
		sharepointDomain: yup
			.string()
			.trim()
			.lowercase()
			.required('SharePoint domain cannot be empty')
			.test('https', 'SharePoint domain must start with https://', (value) =>
				value.startsWith('https://'),
			)
			.test(
				'no-trailing-slash',
				'SharePoint domain cannot end with /',
				(value) => !value.endsWith('/'),
			)
			.test('sharepoint', 'SharePoint domain must end with .sharepoint.com', (value) =>
				value.endsWith('.sharepoint.com'),
			)
			.matches(
				/^https:\/\/[a-z0-9]+\.sharepoint\.com$/,
				'SharePoint domain must be https://<tenant>.sharepoint.com, with a tenant name of only letters and numbers',
			),
		clientSiteSuffix: yup
			.string()
			.trim()
			.test(
				'sites',
				'Client site suffix must start with /sites/',
				(value) => !value || value.startsWith('/sites/'),
			)
			.test(
				'no-trailing-slash',
				'Client site suffix cannot end with /',
				(value) => !value || !value.endsWith('/'),
			)
			.matches(/^\/sites\/[A-Za-z0-9_-]+$/, {
				message:
					'Client site suffix must be /sites/<site name>, with a site name of only letters, numbers, - and _',
				excludeEmptyString: true,
			}),
		clientFolder: yup
			.string()
			.trim()
			.required('Client folder cannot be empty')
			.test('no-slash', 'Client folder cannot contain /', (value) => !value.includes('/'))
			.test(
				'not-encoded',
				'Enter the client folder as it appears in SharePoint (e.g. Client Documents), not URL-encoded',
				(value) => !value.includes('%'),
			),
		country: yup
			.string()
			.required('Country is required')
			.oneOf(
				Object.keys(countries),
				`Country must be one of ${Object.values(countries)
					.map(({ countryName }) => countryName)
					.join(', ')}`,
			),
		state: yup
			.string()
			.nullable()
			.test('state', function (state) {
				const country = countries[this.parent.country];
				if (!state || !country) {
					return true;
				}
				if (!country.states.length) {
					return this.createError({ message: `State can't be set for ${country.countryName}` });
				}
				return (
					country.states.includes(state) ||
					this.createError({ message: `State must be one of ${country.states.join(', ')}` })
				);
			}),
		timezone: yup
			.string()
			.required('Timezone is required')
			.test('timezone', function (timezone) {
				const mismatch = getTimezoneMismatch(countries, this.parent.country, timezone);
				return !mismatch || this.createError({ message: mismatch });
			}),
	});
